import { useEffect, useState, useCallback } from 'react'
import { Headphones, RefreshCw, Send, ChevronDown, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { supportTicketService } from '../../../services/supportTicketService'
import type { SupportTicket, TicketStatus, TicketPriority } from '../../../types/api.types'

function statusColor(s: TicketStatus): string {
  const map: Record<TicketStatus, string> = {
    OPEN:        'bg-amber-100 text-amber-800',
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    RESOLVED:    'bg-green-100 text-green-800',
    CLOSED:      'bg-gray-100 text-gray-500',
  }
  return map[s] ?? 'bg-gray-100 text-gray-400'
}

function priorityColor(p: TicketPriority): string {
  const map: Record<TicketPriority, string> = {
    CRITICAL: 'bg-red-100 text-red-800',
    HIGH:     'bg-orange-100 text-orange-800',
    MEDIUM:   'bg-amber-100 text-amber-800',
    LOW:      'bg-green-100 text-green-800',
  }
  return map[p] ?? 'bg-gray-100 text-gray-400'
}

function fmtDate(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

interface TicketRowProps {
  ticket: SupportTicket
  onUpdated: (t: SupportTicket) => void
}

function TicketRow({ ticket, onUpdated }: TicketRowProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [replyMsg, setReplyMsg] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [sending, setSending] = useState(false)
  const [newStatus, setNewStatus] = useState<TicketStatus>(ticket.status)
  const [resolutionNotes, setResolutionNotes] = useState('')
  const [updatingStatus, setUpdatingStatus] = useState(false)

  async function sendReply() {
    if (!replyMsg.trim()) return
    setSending(true)
    try {
      const updated = await supportTicketService.reply(ticket.id, replyMsg.trim(), isInternal)
      onUpdated(updated)
      setReplyMsg('')
      toast.success(isInternal ? 'Internal note added.' : 'Reply sent.')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to send reply')
    } finally {
      setSending(false) }
  }

  async function updateStatus() {
    if (newStatus === ticket.status && !resolutionNotes.trim()) return
    setUpdatingStatus(true)
    try {
      const updated = await supportTicketService.updateStatus(
        ticket.id, newStatus, resolutionNotes.trim() || undefined,
      )
      onUpdated(updated)
      toast.success(`Ticket status updated to ${newStatus}.`)
      setResolutionNotes('')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to update status')
    } finally {
      setUpdatingStatus(false)
    }
  }

  return (
    <>
      <tr
        className="cursor-pointer border-t border-gray-100 hover:bg-gray-50/60"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
            <span className="text-[12px] font-mono text-gray-500">{ticket.ticket_number}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <p className="text-[13px] font-medium text-gray-900">{ticket.subject}</p>
          <p className="text-[11px] text-gray-400">{ticket.submitted_by_name} · {ticket.submitted_by_email}</p>
        </td>
        <td className="px-4 py-3 text-[12px] text-gray-600">{ticket.organization_name ?? '—'}</td>
        <td className="px-4 py-3">
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusColor(ticket.status)}`}>
            {ticket.status.replace('_', ' ')}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${priorityColor(ticket.priority)}`}>
            {ticket.priority}
          </span>
        </td>
        <td className="px-4 py-3 text-[12px] text-gray-500">{ticket.category.replace('_', ' ')}</td>
        <td className="px-4 py-3 text-[12px] text-gray-500">{fmtDate(ticket.created_at)}</td>
        <td className="px-4 py-3 text-center text-[12px] text-gray-500">{ticket.replies.length}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} className="bg-gray-50/40 px-4 py-4">
            <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
              {/* Description */}
              <div>
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Description</p>
                <p className="whitespace-pre-wrap text-[13px] text-gray-700">{ticket.description}</p>
              </div>

              {/* Replies */}
              {ticket.replies.length > 0 && (
                <div>
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">Replies</p>
                  <div className="space-y-2">
                    {ticket.replies.map((r) => (
                      <div
                        key={r.id}
                        className={`rounded-lg p-3 text-[13px] ${r.is_internal ? 'border border-amber-200 bg-amber-50' : 'border border-gray-100 bg-gray-50'}`}
                      >
                        <div className="mb-1 flex items-center gap-2 text-[11px] text-gray-500">
                          <span className="font-medium text-gray-700">{r.replied_by_name}</span>
                          {r.is_internal && <span className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] text-amber-800">Internal</span>}
                          <span>{fmtDate(r.created_at)}</span>
                        </div>
                        <p className="whitespace-pre-wrap text-gray-700">{r.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reply form */}
              <div className="border-t border-gray-100 pt-3">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">Reply</p>
                <textarea
                  rows={3}
                  value={replyMsg}
                  onChange={(e) => setReplyMsg(e.target.value)}
                  placeholder="Type your reply..."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
                />
                <div className="mt-2 flex items-center justify-between">
                  <label className="flex cursor-pointer items-center gap-2 text-[12px] text-gray-600">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="h-3.5 w-3.5 rounded"
                    />
                    Internal note (not sent to customer)
                  </label>
                  <button
                    type="button"
                    onClick={sendReply}
                    disabled={sending || !replyMsg.trim()}
                    className="flex items-center gap-1.5 rounded-lg bg-brand-green px-3 py-1.5 text-[12px] font-semibold text-black hover:opacity-80 disabled:opacity-40"
                  >
                    {sending ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                    Send
                  </button>
                </div>
              </div>

              {/* Status update */}
              {ticket.status !== 'CLOSED' && (
                <div className="border-t border-gray-100 pt-3">
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">Update Status</p>
                  <div className="flex flex-wrap items-end gap-3">
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value as TicketStatus)}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
                    >
                      <option value="OPEN">Open</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="RESOLVED">Resolved</option>
                      <option value="CLOSED">Closed</option>
                    </select>
                    {(newStatus === 'RESOLVED' || newStatus === 'CLOSED') && (
                      <input
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        placeholder="Resolution notes..."
                        className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
                      />
                    )}
                    <button
                      type="button"
                      onClick={updateStatus}
                      disabled={updatingStatus || (newStatus === ticket.status && !resolutionNotes.trim())}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-[13px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                      {updatingStatus ? 'Saving...' : 'Update'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

const STATUS_OPTIONS: { value: '' | TicketStatus; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
]

const PRIORITY_OPTIONS: { value: '' | TicketPriority; label: string }[] = [
  { value: '', label: 'All Priorities' },
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
]

export function PlatformSupportPage(): JSX.Element {
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<'' | TicketStatus>('')
  const [priorityFilter, setPriorityFilter] = useState<'' | TicketPriority>('')

  const load = useCallback(async (status?: TicketStatus | '', priority?: TicketPriority | '') => {
    setLoading(true)
    try {
      const res = await supportTicketService.listAll({
        ticket_status: status || undefined,
        priority: priority || undefined,
        limit: 100,
      })
      setTickets(res.tickets)
      setTotal(res.total)
    } catch {
      setTickets([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(statusFilter, priorityFilter) }, [statusFilter, priorityFilter, load])

  function handleUpdated(updated: SupportTicket) {
    setTickets((prev) => prev.map((t) => t.id === updated.id ? updated : t))
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Support Tickets</h1>
          <p className="mt-1 text-sm text-gray-500">
            {total} ticket{total !== 1 ? 's' : ''} total
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(statusFilter, priorityFilter)}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Headphones size={15} className="text-gray-400" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | TicketStatus)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
        >
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as '' | TicketPriority)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
        >
          {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {(statusFilter || priorityFilter) && (
          <button
            type="button"
            onClick={() => { setStatusFilter(''); setPriorityFilter('') }}
            className="text-[12px] text-gray-400 hover:text-gray-600"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white">
        {loading ? (
          <p className="py-12 text-center text-[13px] text-gray-400">Loading tickets...</p>
        ) : tickets.length === 0 ? (
          <p className="py-12 text-center text-[13px] text-gray-400">No tickets found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Ticket #</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Subject / Submitter</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Organisation</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Status</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Priority</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Category</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Submitted</th>
                  <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500">Replies</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <TicketRow key={t.id} ticket={t} onUpdated={handleUpdated} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
